import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { Observable } from 'rxjs/internal/Observable';

import { ApiResponse } from '@app/_models';

@Injectable({
  providedIn: 'root'
})
export class TestCaseService {

  constructor(private http: HttpClient) { }

  getTestCaseList(searchParams: any): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}testCases/list`, searchParams);
  }

  getTestCaseListById(id: number): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}testCases/${id}`);
  }

  saveTestCaseList(obj: any): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}testCases`, obj);
  }

  updateTestCases(obj: any): Observable<any> {
    return this.http.put<any>(`${environment.apiUrl}testCases`, obj);
  }

  getShortData(id: number): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}testCases/${id}/testCaseEndPoints/shortData`);
  }

  getParticularExecution(id: string, testcaseID: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}result/endPoints/endPointSqlId/${id}/testCaseEndPointId/${testcaseID}/lastExecution`);
  }

  getAssociateData(postData: any): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}endPoints/list`, postData);
  }

  getAssociateShortData(id: number): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}testCases/${id}/testCaseEndPoints/shortData`);
  }

  saveAssociateShortData(id: number, shortData: any): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}testCases/${id}/testCaseEndPoints/shortData`, shortData);
  }

  getAssociatedWithData(id: string, data: any): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}testCases/${id}/testSuites`, data);
  }

  savetestCase(params: any): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}testCases`, params);
  }

  getEnv(id: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}environmentMaps/projects/${id}`);
  }

  getEnvByTestcaseId(id: string, testCaseId : any): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}environmentMaps/projects/${id}?testCaseId=${testCaseId}`);
  }

  executorStart(testCaseId: string, environmentId: string, params: any): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}executors/testCases/${testCaseId}/environmentMappers/${environmentId}`, params);
  }

  getExecutorDetails(noSqlId: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}result/executions/${noSqlId}`);
  }

  readResultFromMongoDb(id: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}result/testCases/${id}/currentExecutionStatus`);
  }

  lastExecution(sqlId: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}result/testCases/sqlId/${sqlId}/lastExecution`);
  }

  getAssociatedTestsuites(testcaseId: number, searchParams: any): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}testCases/${testcaseId}/testSuites`, searchParams);
  }

  /* Test Data */
  getTestDataByTestCase(testCaseId: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}testCases/${testCaseId}/testCaseEndPointTestInputs`);
  }

  getTestDataByTestcaseEndpoint(testcaseEndpointId: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}testInputs/testCaseEndPoints/${testcaseEndpointId}`);
  }

  /* Validations */
  getValidationByTestcaseId(testCaseId: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}validations/testCase/${testCaseId}`);
  }

  cloneValidation(validation: any): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}validations/clone`, validation);
  }

  deleteMultipleValidations(validations: any): Observable<any> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: validations,
    };
    return this.http.delete<any>(`${environment.apiUrl}validations`, options);
  }

  deleteValidation(id: any) {
    return this.http.delete<any>(`${environment.apiUrl}validations/${id}`);
  }

  deleteMultipleTestcases(entityType: string, entityIdList: number[]): Observable<any> {
    const options = {
      body: {
        entityType,
        entityIdList
      }
    };
    return this.http.delete<any>(`${environment.apiUrl}testCases/bulkDelete`, options);
  }

  createValidation(validation: any): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}validations`, validation);
  }

  updateValidation(validation: any): Observable<any> {
    return this.http.put<any>(`${environment.apiUrl}validations`, validation);
  }

  /* Response Extractors */
  readSyncRespExtractor(testCaseId: string, filterInactive: boolean = false): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}responseExtractors/testCases/${testCaseId}/testCaseEndPointShortDataWithResponseExtractors?filterInactive=${filterInactive}`);
  }

  updateSyncRespExtractor(testCaseEndPointId: string, testCaseResponseExtractors: object): Observable<any> {
    return this.http.put<any>(`${environment.apiUrl}responseExtractors/testCaseEndPoints/${testCaseEndPointId}`, testCaseResponseExtractors);
  }

  getShortDataWithResponseExtractor(testcaseId: number) {
    return this.http.get<any>(`${environment.apiUrl}responseExtractors/testCases/${testcaseId}/testCaseEndPointShortDataWithResponseExtractors`);
  }

  updateJsExpressionList(endpointId: string, requestParams: any): Observable<any> {
    return this.http.put<any>(`${environment.apiUrl}jsExperssion/testCaseEndPoints/${endpointId}`, requestParams);
  }

  assocDissocBrowserExtensions(formData: any): Observable<any> {
    return this.http.put<any>(`${environment.apiUrl}testCaseEndpoints/browserExtensionAssociationDisassociation`, formData);
  }

  getBrowserExtensions(id: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}projects/${id}/browserExtensions`);
  }

  getBrowserWebEndpoints(id: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}testCases/${id}/webTestCaseEndPoints`);
  }
  getProjectWebConfig(id) {
    return this.http.get<any>(`${environment.apiUrl}project/config/webConfig/projects/${id}`);
  }
  getExecutionResult(noSqlId: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}result/testCases/executions/${noSqlId}`);
  }

  getExecutionDetails(noSqlId: string, retryAttempt?: number) {
    if(retryAttempt === undefined) {
      return this.http.get<any>(`${environment.apiUrl}result/testCases/testdata/execution/${noSqlId}`);
    } else {
      return this.http.get<any>(`${environment.apiUrl}result/testCases/testdata/execution/${noSqlId}?retryAttempt=${retryAttempt}`);
    }
  }

  getExecutionDetailsWithFilter(noSqlId: string, retryAttempt?: number, endpointStatus?: string) {
    if(retryAttempt === undefined) {
      return this.http.get<any>(`${environment.apiUrl}result/testCases/testdata/execution/${noSqlId}/executionstatus?status=${endpointStatus}`);
    } else {
      return this.http.get<any>(`${environment.apiUrl}result/testCases/testdata/execution/${noSqlId}/executionstatus?retryAttempt=${retryAttempt}&status=${endpointStatus}`);
    }
  }

  readEndpointsFromNoSql(noSqlId: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}result/testCases/${noSqlId}/endPointShortData`);
  }

  stopCurrentExecService(noSqlId: string): Observable<any> {
    return this.http.put<any>(`${environment.apiUrl}result/executions/${noSqlId}/stop`, noSqlId);
  }

  getLoadTesting(id: number): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}testCases/${id}/loadTestSettings`);
  }

  saveLoadTesting(id: string, formData: any): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}testCases/${id}/loadTestSettings`, formData);
  }

  UpdateLoadTesting(id: string, formData: any): Observable<any> {
    return this.http.put<any>(`${environment.apiUrl}testCases/${id}/loadTestSettings`, formData);
  }

  getLoadConfiguration(id: number): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}performance/loadtest/testCase/${id}`);
  }
  cloneLoadConfiguration(id: number, configId:number): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}performance/loadtest/clone/${id}/${configId}`);
  }

  saveLoadConfiguration(id: string, formData: any): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}performance/loadtest/${id}`, formData);
  }

  updateLoadConfiguration(id: string, formData: any): Observable<any> {
    return this.http.put<any>(`${environment.apiUrl}performance/loadtest/${id}`, formData);
  }

  deleteLoadConfiguration(id: string): Observable<any> {
    return this.http.delete<any>(`${environment.apiUrl}performance/loadtest/${id}`);
  }

  fileUpload(testCaseEndPointLoadTestConfigId: string, file: any): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}file/loadTest/${testCaseEndPointLoadTestConfigId}`, file);
  }

  deleteFile(testCaseEndPointLoadTestConfigId: string): Observable<any> {
    return this.http.delete<any>(`${environment.apiUrl}file/loadTest/${testCaseEndPointLoadTestConfigId}`);
  }

  deleteLoadTest(id: string): Observable<any> {
    return this.http.delete<any>(`${environment.apiUrl}testCases/${id}/loadTestSettings`);
  }

  getTestData(testcaseId: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}testCases/${testcaseId}/testCaseEndPointTestInputs`);
  }

  updateTestData(testCaseId: string, data: any): Observable<any> {
    return this.http.put<any>(`${environment.apiUrl}testCases/${testCaseId}/testCaseEndPointTestInputs`, data);
  }

  updateAllResponseExtractors(data: any): Observable<any> {
    return this.http.put<any>(`${environment.apiUrl}responseExtractors`, data);
  }

  resumeCurrentExecService(executionId: string): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}executors/resume/executions/${executionId}`, null);
  }

  testDataFileUpload(testCaseId: string, selectedOption: string, data: FormData): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}testCases/importExcel/${testCaseId}/${selectedOption}`, data);
  }

  testDataExport(testcaseId: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}testCases/${testcaseId}/generateExcel`);
  }

  importTestcaseByPostmanIntegration(projectId: number, file: any, forceCreate: boolean): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}postmanIntegration/testCase/import?projectId=${projectId}&forceCreate=${forceCreate}`, file);
  }

  importTestcaseRelayData(projectId: number, file: any, forceCreate: boolean): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}testCases/import?projectId=${projectId}&forceCreate=${forceCreate}`, file);
  }

  saveTestcaseByPostmanIntegration(projectId: number, data: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}postmanIntegration/testCase/create?projectId=${projectId}`, data);
  }

  saveTestcaseByRelayImport(projectId: number, data: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}testCases/import/create?projectId=${projectId}`, data);
  }

  getFilesByProject(projectId: number): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${environment.apiUrl}file/${projectId}`);
  }

  getQ2cFiles(id: number): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${environment.apiUrl}q2c/testCases/${id}/files/shortData`);
  }

  saveQ2cFiles(id: number, data): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}q2c/testCases/${id}/files/shortData`, data);
  }

  cloneResponseExtractor(testCaseEndPointId: number, testCaseEndPointShortData: any, ignore: boolean, responseExtractorId: number): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}responseExtractors/testCaseEndpoint/${testCaseEndPointId}/extractor/${responseExtractorId}/clone?ignore=${ignore}`, testCaseEndPointShortData);
  }

  getTestCaseEndPointList(testCaseEndPointId: number): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${environment.apiUrl}testCaseEndpoints/${testCaseEndPointId}`);
  }

  cloneValidationAcrossTestCases(validationId: number, testCaseEndPointShortDataList: any, ignore: boolean): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}validations/${validationId}/cloneAcrossTestCase?ignore=${ignore}`, testCaseEndPointShortDataList);
  }

  saveBulkValidation(obj: any): Observable<any> {
    return this.http.put<ApiResponse>(`${environment.apiUrl}validations/updateInBulk`, obj);
  }

  /* import and export js expression section */
  importJsExpression(id: number, data: object): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}jsExperssion/importBulkJsExpression/${id}`, data);
  }

  /* Rules section */
  getRulesData(id: number, clone: boolean = false): Observable<ApiResponse> {
    return this.http.get<any>(`${environment.apiUrl}testCaseEndpoints/${id}/action?clone=${clone}`);
  }

  saveRules(id: number, data: object): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}testCaseEndpoints/${id}/action`, data);
  }

  updateRules(id: number, data: object): Observable<any> {
    return this.http.put<any>(`${environment.apiUrl}testCaseEndpoints/${id}/action`, data);
  }

  deleteRule(id: number) {
    return this.http.delete<any>(`${environment.apiUrl}testCaseEndpoints/${id}/action`);
  }

  getExecutionHistory(searchParams:any): Observable<ApiResponse> {
    return this.http.post<any>(`${environment.apiUrl}executions/result`,searchParams);
  }

  getUsersList() {
    return this.http.get<any>(`${environment.apiUrl}users`);
  }

  exportSchema(noSqlId: any): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}result/testCases/${noSqlId}/exportSchema`);
  }

  getExecCompareTestCaseData(leftTestCaseNoSql: any, rightTestCaseNoSql: any) {
    return this.http.get<any>(`${environment.apiUrl}result/testCases/compare/${leftTestCaseNoSql}/${rightTestCaseNoSql}`);
  }

  triggerExecCompareEndpointData(data: any): Observable<ApiResponse> {
    return this.http.post<any>(`${environment.apiUrl}result/endPoints/compare`, data);
  }

  getExecCompareEndpointData(executionId: any, status? : any): Observable<ApiResponse> {
    if(status === undefined) {
      return this.http.get<any>(`${environment.apiUrl}result/endPoints/compare/${executionId}`);
    } else {
      return this.http.get<any>(`${environment.apiUrl}result/endPoints/compare/${executionId}?status=${status}`);
    }
  }

  getDetailedTestData(request: any) {
    return this.http.post<any>(`${environment.apiUrl}result/endPoints/compare/data`, request);
  }

  exportTestData(id){
    return this.http.get<any>(`${environment.apiUrl}testCases/export/${id}`);
  }

  getTestcaseComplexityScore(id: any) {
    return this.http.get<any>(`${environment.apiUrl}testCase/complexity/${id}`);
  }

  createJiraDefectManually(testcaseId: any) {
    return this.http.post<any>(`${environment.apiUrl}jira/create/manualDefect/${testcaseId}`, {});
  }

  saveRulesList(request: any, testCaseEndPointId: any) {
    return this.http.post<any>(`${environment.apiUrl}testCaseEndpoints/${testCaseEndPointId}/list/action`, request);
  }

  getTestDataList(testcaseId: any) {
    return this.http.get<any>(`${environment.apiUrl}testCases/${testcaseId}/testCaseTestDataSequence`);
  }

  getTestcaseMobilePerformanceGraph(nosqlId) {
    return this.http.get<any>(`${environment.apiUrl}mobilePerformance/testcase/testCaseExecution/${nosqlId}/graph`);
  }
}
